/**
 * Application Insights singleton.
 *
 * Initialised once at module load time so every part of the app shares the
 * same telemetry client.  When VITE_APPINSIGHTS_CONNECTION_STRING is absent
 * (e.g. local development without a real AI resource) the SDK is intentionally
 * left un-initialised and all exported helpers become no-ops.
 */
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import {
  OfflineChannel,
  StorageProviders,
} from "@microsoft/applicationinsights-offlinechannel-js";
import { runtimeEnv } from "@/utils/runtimeEnv";
import { resolveDeviceClass } from "@/utils/deviceClass";
import { BUILD_ID } from "@/utils/buildId";
import { useAppStore } from "@/store/appStore";

const connectionString =
  runtimeEnv.VITE_APPINSIGHTS_CONNECTION_STRING || undefined;

let appInsights: ApplicationInsights | null = null;

if (connectionString) {
  const offlineChannel = new OfflineChannel();

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      // Automatic route-change page views are handled manually in
      // useAppInsightsPageTracking so that we can supply a clean page name.
      enableAutoRouteTracking: false,
      // Automatically capture unhandled promise rejections and JS exceptions.
      enableUnhandledPromiseRejectionTracking: true,
      // Capture dependency calls (fetch / XHR) automatically.
      disableFetchTracking: false,
      disableAjaxTracking: false,
      extensionConfig: {
        [offlineChannel.identifier]: {
          providers: [StorageProviders.LocalStorage],
        },
      },
    },
  });
  appInsights.loadAppInsights();
  // Must be added after loadAppInsights so the sender channel is initialised first.
  appInsights.addPlugin(offlineChannel);

  // application.ver surfaces as the "Application version" column in the portal,
  // which is what identifies the source map to unminify a stack against.
  appInsights.context.application.ver = BUILD_ID;

  // Stamp the hashed user id and the user's current offline context onto every
  // telemetry item. The hashed id correlates with server-side request telemetry
  // (which carries the same hash); the offline dimensions let admin activity
  // queries flag telemetry generated while the user was offline. Connectivity is
  // read at track time, so queued offline telemetry keeps its original timestamp
  // and the offline flag it had when it occurred.
  appInsights.addTelemetryInitializer((envelope) => {
    const data: Record<string, unknown> = { ...envelope.data };
    // Build id on every item: a minified stack is only resolvable against the
    // source map of the build that emitted it.
    data.buildId = BUILD_ID;
    if (hashedUserId) {
      data.hashedUserId = hashedUserId;
    }
    const { isOffline, isManualOffline, isTrulyOffline, offlineSessionId, selectedLanguage } =
      useAppStore.getState();
    // Stamp the content language so page-view aggregates can be filtered by language.
    if (selectedLanguage) {
      data.languageCode = selectedLanguage;
    }
    data.offlineMode =
      isManualOffline && isTrulyOffline
        ? "both"
        : isManualOffline
          ? "manual"
          : isTrulyOffline
            ? "network"
            : "none";
    if (isOffline && offlineSessionId) {
      data.offlineSessionId = offlineSessionId;
    }
    envelope.data = data;
  });
}

// Latest hashed user id, set by the auth flow once identity is known.
let hashedUserId: string | null = null;

/**
 * Set (or clear) the hashed user id attached to page-view telemetry. Called by
 * the auth store when identity loads/rehydrates, and cleared on reset/logout.
 */
export function setTelemetryUserId(id: string | null): void {
  hashedUserId = id;
}

// ── Public helpers ──────────────────────────────────────────────────────────

/**
 * Track a page view.  Call this after every React Router navigation.
 *
 * @param name   Human-readable page name, e.g. "Practice Area – Engineering"
 * @param url    The current URL path (defaults to window.location.pathname)
 */
export function trackPageView(
  name: string,
  url?: string,
  properties?: Record<string, string>,
): void {
  appInsights?.trackPageView({
    name,
    uri: url ?? window.location.pathname,
    properties: { ...properties, deviceClass: resolveDeviceClass() },
  });
}

/**
 * Track a discrete user interaction (button click, toggle, etc.).
 *
 * @param name        Event name, e.g. "Go Offline"
 * @param properties  Optional bag of extra dimensions / metrics
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string | number | boolean>,
): void {
  appInsights?.trackEvent({ name }, properties);
}

/**
 * Track an exception. Used by the error boundary and global error handlers.
 *
 * @param error        The Error object to track
 * @param properties   Optional custom dimensions / metrics
 */
export function trackException(
  error: Error,
  properties?: Record<string, string | number | boolean>,
): void {
  appInsights?.trackException({ exception: error }, properties);
}

/**
 * Expose the underlying SDK instance for advanced use-cases.
 * Returns null when App Insights is not configured.
 */
export { appInsights };
